import { ref, nextTick } from 'vue';

import { BOOTSTRAP_READY_TIMEOUT_MS } from '@/constants/auth';
import { captureAttributionFromUrl } from '@/helpers/attribution';
import { captureInviteFromUrl } from '@/helpers/inviteCode';
import { captureReferralCodeFromUrl } from '@/helpers/referralCode';
import { oauthQueryRescue } from '@/router/oauthQueryRescue';
import { resolveAuthedToRedirect, resolveSignedInLanding } from '@/router/utils';

import { bpSelection } from './bpSelection';
import { crossTabSync } from './crossTabSync';
import { businessProfileIdFromToken } from './jwtExpiry';
import { acceptPendingInvite } from './pendingInvite';
import { profileTokens } from './profileTokens';
import { session } from './session';
import { tokenStorage } from './tokenStorage';

// Gates only the DESTRUCTIVE branch of migrateSessionDedup (forced local re-login on diverged
// chains) so it runs at most once per install; the safe duplicate-drop runs on every boot.
// Note: logout's tokenStorage.clearAll() wipes the whole wp.auth. namespace INCLUDING this
// marker, so the destructive branch re-arms after every logout. That is deliberate-adjacent and
// harmless: it only fires when a session AND a diverged wp.auth.bp.<sessionBp> duplicate coexist,
// and a fresh login on a fixed build cannot produce such a duplicate — while an old-build tab
// that recreates one is exactly the case we WANT cleaned again.
const AUTH_SCHEMA_VERSION = '2';
const AUTH_SCHEMA_VERSION_KEY = 'wp.auth.schemaVersion';

// Older builds copied the login token into BOTH wp.auth.session and a wp.auth.bp.<default> store,
// which then rotated independently under separate refresh locks and tripped backend
// reuse-detection (spontaneous logout after ~10-15 min). The invariant is "no wp.auth.bp entry
// may share the session's refresh chain", enforced on EVERY boot (self-healing — a duplicate can
// reappear via a tab still on the old build, or a legacy flat-key promotion by migrateLegacy),
// so this must run AFTER migrateLegacy() and before hydrate(). The check is cheap: one
// loadSession + one JWT decode + one loadBPToken.
function migrateSessionDedup() {
  const sessionEntry = tokenStorage.loadSession();
  const sessionBpId = businessProfileIdFromToken(sessionEntry?.accessToken);
  const duplicate = sessionBpId != null ? tokenStorage.loadBPToken(sessionBpId) : null;
  if (sessionEntry?.refreshToken && duplicate) {
    if (duplicate.refreshToken === sessionEntry.refreshToken) {
      // Same refresh chain → the BP entry is a pure duplicate; dropping it is always safe.
      tokenStorage.removeBPToken(sessionBpId);
    } else if (localStorage.getItem(AUTH_SCHEMA_VERSION_KEY) !== AUTH_SCHEMA_VERSION) {
      // The two chains already rotated apart to different refresh tokens — the shared-sid split.
      // This is the common state for any old-build session that refreshed at least once (the sid is
      // likely already reuse-flagged backend-side), so a one-time forced local re-login is the safe,
      // deterministic outcome rather than guessing which token is current. Expect a single wave of
      // re-logins on the deploy that ships this migration. One-time (schemaVersion-gated) so a
      // FUTURE legitimately-diverged entry can never wipe a live session again.
      tokenStorage.clearAll();
      profileTokens.clearAll();
      session.clearSession();
    }
  }
  localStorage.setItem(AUTH_SCHEMA_VERSION_KEY, AUTH_SCHEMA_VERSION);
}

const ready = ref(false);
let readyResolve;
const readyPromise = new Promise((r) => {
  readyResolve = r;
});

// Idempotent: flips ready + resolves the gate exactly once. Called from the finally below and
// from the watchdog, so the router guard never waits forever no matter how installAuth ends.
function markReady() {
  if (ready.value) return;
  ready.value = true;
  readyResolve();
}

export async function installAuth(router) {
  const watchdog = setTimeout(markReady, BOOTSTRAP_READY_TIMEOUT_MS);
  try {
    // Seed the synchronous BP-selection intent before any BP-resolving request fires, from
    // the only sources available pre-router: the URL query, then persisted selection. Readers
    // (tokenService / auth) consult bpSelection instead of window.location once migrated.
    bpSelection.seedFromEnvironment();

    oauthQueryRescue.captureFromUrl();

    // Persist a referral/promo code from the landing URL before navigation strips it,
    // so it's still available when the user reaches registration later. See issue #722.
    captureReferralCodeFromUrl();

    // Persist an invite code from the email link before navigation strips it — it is accepted
    // post-auth (here after restore for an already-logged-in user, and in the login flows).
    captureInviteFromUrl();

    // Persist first-touch marketing attribution (UTM / click ids / referrer / landing page)
    // from the landing URL before navigation strips the query string, so it can ride the
    // /auth/confirm request when the user completes sign-up.
    captureAttributionFromUrl();

    crossTabSync.connect({
      onSignIn: async () => {
        await session.restore(); // deduped internally
        // Follow the sign-in: resume this tab's destination, or go home. nextTick lets
        // the store's CASL-sync watch flush so the landing route sees the updated abilities.
        await nextTick();
        const current = router.currentRoute.value;
        if (current.meta?.redirectIfLoggedIn || current.name === 'login' || current.name === 'register') {
          // The account store's login watcher skips auth pages (their own flows sync), so load
          // the business profiles here before leaving one. Lazy import (as in tokenService) keeps
          // the account store out of the auth bootstrap's early module graph.
          const { useAccountStore } = await import('@/stores/account.js');
          const accountStore = useAccountStore();
          await accountStore.syncBusinessProfiles().catch((e) => {
            console.error('[auth] cross-tab sign-in profile sync failed:', e);
          });
          // Same landing as a sign-in in this tab (seller-only / eBay-only profiles open their own
          // pages), without the connect-accounts onboarding: that belongs to the tab that signed in.
          const [{ isEbayPagesAllowed }, { useAuthStore }, { useEnvironmentStore }] =
            await Promise.all([
              import('@/router/devOnlyPaths'),
              import('@/stores/auth.js'),
              import('@/stores/environment.js'),
            ]);
          const ebayAllowed = isEbayPagesAllowed(useAuthStore(), useEnvironmentStore());
          if (current.query.to) {
            router.push(
              await resolveAuthedToRedirect(current, { landing: { accountStore, ebayAllowed } }),
            );
            return;
          }
          router.push(await resolveSignedInLanding(accountStore, { ebayAllowed }));
        }
      },
      onSignOut: () => session.logout({ source: 'broadcast', redirect: true }), // no re-broadcast
      onTokensUpdated: (bpId) => profileTokens.syncFromStorage(bpId),
    });

    session.setRouter(router);

    tokenStorage.resolveStore(); // point at the backend (local/session) holding the live session
    tokenStorage.migrateLegacy(); // one-time: flat keys -> wp.auth.bp.<bpId>, drop mirror keys
    // AFTER migrateLegacy — a default-BP duplicate promoted from legacy flat keys must be visible
    // to the dedup, or it would be resurrected right after the check (see migrateSessionDedup).
    migrateSessionDedup(); // every boot: drop the default-BP token duplicate (shared-sid fix)
    profileTokens.hydrate();

    await session.restore();

    // Already-logged-in user opened an invite link: accept the captured invite now.
    await acceptPendingInvite();
  } catch (e) {
    console.error('[auth] bootstrap failed:', e);
  } finally {
    clearTimeout(watchdog);
    markReady();
  }
}

export const bootstrap = {
  ready,
  waitForReady: () => readyPromise,
};
